"use client";

import React from "react";
import { Check, Sparkles, Shield, ArrowRight, Stethoscope } from "lucide-react";
import { DENTAL_TREATMENTS, DentalTreatment } from "@/lib/demos/dentalData";

interface DentalServicesSectionProps {
  currency: "INR" | "USD";
  onSelectTreatment: (treatment: DentalTreatment) => void;
}

export default function DentalServicesSection({
  currency,
  onSelectTreatment,
}: DentalServicesSectionProps) {
  const sym = currency === "USD" ? "$" : "₹";

  return (
    <section id="specialty-treatments" className="py-16 sm:py-24 bg-slate-950 text-white border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14 space-y-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/30 text-[11px] font-bold text-teal-400 uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Specialty Clinical Services</span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-black font-heading tracking-tight text-white">
            Precision Cosmetic &amp; Surgical Dentistry
          </h2>

          <p className="text-xs sm:text-sm text-slate-400">
            Engineered with Swiss Nobel Biocare implants, German Carl Zeiss magnification, and Class-B autoclave medical sterility.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {DENTAL_TREATMENTS.slice(0, 3).map((treatment) => {
            const price = currency === "USD" ? treatment.priceUSD : treatment.priceINR;
            const isFeatured = treatment.id === "veneer-makeover";

            return (
              <div
                key={treatment.id}
                className={`relative rounded-3xl p-7 flex flex-col justify-between transition-all duration-300 ${
                  isFeatured
                    ? "bg-gradient-to-b from-slate-900 to-slate-950 border-2 border-teal-500/60 shadow-2xl shadow-teal-500/10 scale-[1.02]"
                    : "bg-slate-900/60 border border-slate-800 hover:border-slate-700"
                }`}
              >
                {treatment.badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="px-3.5 py-1 rounded-full bg-gradient-to-r from-teal-500 to-teal-600 text-slate-950 text-[10px] font-black tracking-wider uppercase shadow-md flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-slate-950" />
                      {treatment.badge}
                    </span>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Category & Title */}
                  <div>
                    <span className="text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded bg-slate-800 text-teal-300 mb-2 inline-block">
                      {treatment.category}
                    </span>
                    <h3 className="text-xl font-bold text-white mb-2">{treatment.name}</h3>
                    <p className="text-xs text-slate-400 leading-relaxed min-h-[36px]">
                      {treatment.tagline}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="pt-2 border-t border-slate-800/80">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-3xl sm:text-4xl font-black text-white">
                        {sym}{price.toLocaleString()}
                      </span>
                      <span className="text-xs text-slate-400 font-medium">/ Complete Case</span>
                    </div>
                    <span className="text-[10px] text-teal-400 font-semibold mt-1 block">
                      {treatment.warranty}
                    </span>
                  </div>

                  {/* Clinical Specifications */}
                  <div className="space-y-2 text-[11px] text-slate-300 bg-slate-950/80 p-3 rounded-xl border border-slate-800">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Duration:</span>
                      <span className="font-semibold text-slate-200">{treatment.duration}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Anesthesia:</span>
                      <span className="font-semibold text-teal-300">{treatment.anesthesia}</span>
                    </div>
                  </div>

                  {/* Inclusions List */}
                  <div className="space-y-3 pt-1">
                    <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wider block">
                      Clinical Highlights:
                    </span>
                    <ul className="space-y-2.5">
                      {treatment.highlights.map((feat, idx) => (
                        <li key={idx} className="flex items-start gap-2.5 text-xs text-slate-300">
                          <div className="w-4 h-4 rounded-full bg-teal-500/10 border border-teal-500/30 flex items-center justify-center text-teal-400 shrink-0 mt-0.5">
                            <Check className="w-2.5 h-2.5" />
                          </div>
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Bottom CTA */}
                <div className="pt-8">
                  <button
                    onClick={() => onSelectTreatment(treatment)}
                    className={`w-full py-3 px-4 rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-2 ${
                      isFeatured
                        ? "bg-teal-500 hover:bg-teal-400 text-slate-950 shadow-lg shadow-teal-500/20"
                        : "bg-slate-800 hover:bg-slate-700 text-white border border-slate-700"
                    }`}
                  >
                    <span>Book 3D Consultation</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom 2 Services Banner */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
          {DENTAL_TREATMENTS.slice(3).map((treatment) => {
            const price = currency === "USD" ? treatment.priceUSD : treatment.priceINR;

            return (
              <div
                key={treatment.id}
                className="p-6 rounded-3xl bg-slate-900/40 border border-slate-800 flex flex-col justify-between space-y-4 hover:border-slate-700 transition-colors"
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded bg-slate-800 text-teal-300">
                      {treatment.category}
                    </span>
                    <span className="text-lg font-black text-white">
                      {sym}{price.toLocaleString()}
                    </span>
                  </div>
                  <h4 className="text-base font-bold text-white">{treatment.name}</h4>
                  <p className="text-xs text-slate-400">{treatment.tagline}</p>
                </div>

                <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between">
                  <span className="text-[11px] text-teal-400 font-semibold">{treatment.duration}</span>
                  <button
                    onClick={() => onSelectTreatment(treatment)}
                    className="text-xs font-bold text-teal-400 hover:text-teal-300 flex items-center gap-1"
                  >
                    <span>Consult Now</span>
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
